import React, { useEffect, useId, useRef, useState } from "react";
import {
  ACCEPT_UI_SRC,
  ACCEPT_CREDENTIALS,
  hasAcceptCredentials,
} from "../api/authorizeNet";

/**
 * Authorize.Net Accept UI payment button.
 *
 * Card fields render inside an Authorize.Net-hosted iframe modal, so the PAN
 * never enters our DOM and we stay in PCI SAQ A scope. The component yields a
 * single-use payment nonce (`opaqueData`) via `onTokenReceived`; it is valid for
 * 15 minutes and chargeable exactly once.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SCRIPT IS INJECTED *AFTER* THE BUTTON RENDERS
 * ---------------------------------------------------------------------------
 * AcceptUI.js binds itself like this (deobfuscated from the real library):
 *
 *   function t(){
 *     var el = document.getElementsByClassName("AcceptUI")[0];  // ONE-TIME, [0]
 *     bind(el, "click", openModal);
 *     bind(window, "message", onMessage);
 *     readDataAttributes(el);                                   // CACHED here
 *   }
 *   document.readyState === "complete" ? t() : onreadystatechange = ... t()
 *
 * Three consequences that dictate this component's shape:
 *
 * 1. `t()` runs ONCE, and in an SPA `document.readyState` is already "complete"
 *    before React renders anything. So the button must be in the DOM *before*
 *    the script evaluates. Gating the button's render on "script loaded" — the
 *    intuitive order — guarantees the lookup finds nothing and the click is a
 *    silent no-op. We therefore render the button immediately and append the
 *    script from an effect, which runs after the DOM is committed.
 *
 * 2. It reads `[0]` — only the FIRST `.AcceptUI` element on the page. Never
 *    mount two of these at once.
 *
 * 3. It CACHES the `data-*` attributes at bind time. Anything derived from
 *    changing state (e.g. the cart total) would go stale, and there is no
 *    re-init API. So every `data-*` value here is static; the amount lives on
 *    our own button label, which React re-renders correctly.
 *
 * The response handler is resolved by NAME off `window`
 * (`data-responseHandler="..."`) — there is no callback-passing API — so we
 * register a uniquely-named global for the component's lifetime.
 */

const SCRIPT_MARKER = "data-fp-acceptui";

const AuthorizeNetPaymentForm = ({
  amountLabel,
  disabled = false,
  buttonText = "Pay",
  onTokenReceived,
  onError,
}) => {
  // Stable, collision-free suffix per mounted instance.
  const reactId = useId();
  const handlerName = `fpAcceptResponse_${reactId.replace(/[^a-zA-Z0-9]/g, "")}`;

  const [scriptReady, setScriptReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Keep the latest callbacks in a ref so the global handler never closes over
  // a stale render's props.
  const callbacks = useRef({ onTokenReceived, onError });
  callbacks.current = { onTokenReceived, onError };

  // Remounting re-injects the script, which can leave a duplicate `message`
  // listener on window from the previous instance. That would deliver the same
  // nonce twice; this drops the repeat. (The checkout screen also guards with
  // its own in-flight ref, and the nonce is single-use server-side.)
  const lastNonce = useRef(null);

  // --- global response handler ---------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    window[handlerName] = (response) => {
      const { messages, opaqueData } = response || {};

      if (!messages || messages.resultCode !== "Ok") {
        const text =
          (messages &&
            Array.isArray(messages.message) &&
            messages.message.map((m) => m.text).join(" ")) ||
          "We couldn't process those card details. Please check them and try again.";
        callbacks.current.onError?.(text);
        return;
      }

      if (!opaqueData?.dataDescriptor || !opaqueData?.dataValue) {
        callbacks.current.onError?.(
          "The payment library returned an unexpected response. Please try again.",
        );
        return;
      }

      if (lastNonce.current === opaqueData.dataValue) return;
      lastNonce.current = opaqueData.dataValue;

      callbacks.current.onTokenReceived?.({
        dataDescriptor: opaqueData.dataDescriptor,
        dataValue: opaqueData.dataValue,
      });
    };

    return () => {
      delete window[handlerName];
    };
  }, [handlerName]);

  // --- script injection (AFTER the button is in the DOM) --------------------
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!hasAcceptCredentials()) return undefined;

    // Drop any previous instance so the library's one-shot initialiser re-runs
    // and binds to THIS mount's button rather than a detached one.
    document
      .querySelectorAll(`script[${SCRIPT_MARKER}]`)
      .forEach((s) => s.remove());

    const script = document.createElement("script");
    script.src = ACCEPT_UI_SRC;
    script.setAttribute(SCRIPT_MARKER, "true");
    script.onload = () => setScriptReady(true);
    script.onerror = () => setLoadFailed(true);
    document.head.appendChild(script);

    // Deliberately NOT removed on unmount: AcceptUI has no teardown, and
    // removing the tag would not unbind anything anyway.
    return undefined;
  }, []);

  if (!hasAcceptCredentials()) {
    // A build missing REACT_APP_AUTHNET_* would otherwise render a button that
    // silently does nothing.
    return (
      <div style={{ color: "#b00020", fontSize: 14 }}>
        Card payments aren’t configured for this environment.
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div style={{ color: "#b00020", fontSize: 14 }}>
        Couldn’t load the secure payment form. Please refresh and try again.
      </div>
    );
  }

  // The button renders even before the script loads -- that ordering is load
  // bearing (see note 1 above). It stays disabled until the library is ready,
  // which is safe: addEventListener binds fine to a disabled element, and a
  // disabled button simply doesn't emit clicks in the meantime.
  //
  // MODAL TEXT WE CONTROL: exactly two strings --
  //   data-acceptUIFormHeaderTxt  (the heading)
  //   data-acceptUIFormBtnTxt     (the submit button)
  // The field labels ("Card Number", "Exp. Date", "Card Code") are rendered by
  // Authorize.Net inside a cross-origin iframe: not changeable, not styleable.
  // Any further guidance has to live on our own page, above this button.
  return (
    <button
      type="button"
      className="AcceptUI"
      disabled={disabled || !scriptReady}
      data-billingAddressOptions='{"show":false,"required":false}'
      data-apiLoginID={ACCEPT_CREDENTIALS.apiLoginID}
      data-clientKey={ACCEPT_CREDENTIALS.clientKey}
      data-acceptUIFormBtnTxt="Pay"
      data-acceptUIFormHeaderTxt="Please enter your card details"
      data-paymentOptions='{"showCreditCard":true,"showBankAccount":false}'
      data-responseHandler={handlerName}
      style={{
        width: "100%",
        padding: "14px 20px",
        borderRadius: 8,
        border: "none",
        fontSize: 16,
        fontWeight: 600,
        cursor: disabled || !scriptReady ? "not-allowed" : "pointer",
        opacity: disabled || !scriptReady ? 0.5 : 1,
        background: "#111",
        color: "#fff",
      }}
    >
      {!scriptReady
        ? "Loading secure payment form…"
        : amountLabel
          ? `${buttonText} · ${amountLabel}`
          : buttonText}
    </button>
  );
};

export default AuthorizeNetPaymentForm;
